import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getMediaTasks, createMediaTask, updateMediaTaskStatus, deleteMediaTask, getGuildMembers, extractErrorMessage } from '../services/api';
import { FaPlus, FaPlay, FaCheck, FaTimes, FaTrash } from 'react-icons/fa';
import UserSelect from '../components/UserSelect';
import { useToast } from '../components/Toast';

const STATUS_CONFIG = {
  pending:   { label: 'Ausstehend',     color: '#faa61a' },
  active:    { label: 'Aktiv',          color: '#7289da' },
  completed: { label: 'Abgeschlossen',  color: '#43b581' },
  failed:    { label: 'Fehlgeschlagen',  color: '#f04747' }
};

const TYPE_CONFIG = {
  edge:    { label: '\u{1F525} Edging',       color: '#faa61a' },
  cum:     { label: '\u{1F4A6} Cum Tribute',   color: '#FF69B4' },
  worship: { label: '\u{1F6D0} Anbetung',      color: '#9B59B6' },
  custom:  { label: '\u{1F4CB} Eigene',        color: '#5865F2' }
};

const CUM_CONDITIONS = [
  { value: 'auf_bildschirm', label: 'Auf Bildschirm' },
  { value: 'auf_ausdruck',   label: 'Auf Ausdruck' },
  { value: 'cum_countdown',  label: 'Cum Countdown' },
  { value: 'ruined_to_photo', label: 'Ruiniert' },
  { value: 'timed_cum',      label: 'Timed' },
  { value: 'no_hands_cum',   label: 'No Hands' },
  { value: 'custom',         label: 'Eigene' }
];

const WORSHIP_CONDITIONS = [
  { value: 'feet_worship',   label: 'F\u00fc\u00dfe' },
  { value: 'body_worship',   label: 'K\u00f6rper' },
  { value: 'worship_mantra', label: 'Mantra' },
  { value: 'custom',         label: 'Eigene' }
];

function MediaTasks({ user }) {
  const toast = useToast();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completingTask, setCompletingTask] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [membersMap, setMembersMap] = useState({});

  const emptyForm = {
    user_id: '',
    type: 'edge',
    instructions: '',
    edge_count: '',
    duration_minutes: '',
    conditions: '',
    media_id: ''
  };
  const [newTask, setNewTask] = useState(emptyForm);

  const emptyComplete = { notes: '', proof_url: '' };
  const [completeData, setCompleteData] = useState(emptyComplete);

  useEffect(() => {
    fetchTasks();
    getGuildMembers().then(data => {
      const map = {};
      for (const m of data) map[m.id] = m.displayName;
      setMembersMap(map);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [filter]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filter !== 'all') params.status = filter;
      const data = await getMediaTasks(params);
      setTasks(data.tasks || []);
    } catch (error) {
      console.error('Error fetching media tasks:', error);
      toast.error(extractErrorMessage(error, 'Medien-Aufgaben konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const resolveUser = (userId) => membersMap[userId] || userId;

  const handleCreate = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setFormError(null);
    try {
      const payload = {
        user_id: newTask.user_id,
        type: newTask.type,
        instructions: newTask.instructions || undefined,
        media_id: newTask.media_id ? parseInt(newTask.media_id, 10) : undefined
      };
      if (newTask.type === 'edge') {
        payload.edge_count = parseInt(newTask.edge_count, 10);
        if (newTask.duration_minutes) payload.duration_minutes = parseInt(newTask.duration_minutes, 10);
      } else if (newTask.type === 'cum') {
        payload.conditions = newTask.conditions || undefined;
      } else if (newTask.type === 'worship') {
        payload.conditions = newTask.conditions || undefined;
        if (newTask.duration_minutes) payload.duration_minutes = parseInt(newTask.duration_minutes, 10);
      } else if (newTask.type === 'custom') {
        if (newTask.duration_minutes) payload.duration_minutes = parseInt(newTask.duration_minutes, 10);
      }
      await createMediaTask(payload);
      setNewTask(emptyForm);
      setShowCreateModal(false);
      toast.success('Medien-Aufgabe erstellt');
      fetchTasks();
    } catch (error) {
      console.error('Error creating media task:', error);
      setFormError(extractErrorMessage(error, 'Fehler beim Erstellen der Medien-Aufgabe'));
    } finally {
      setSaving(false);
    }
  };

  const handleStart = async (id) => {
    try {
      await updateMediaTaskStatus(id, { status: 'active' });
      toast.success('Aufgabe gestartet');
      fetchTasks();
    } catch (error) {
      console.error('Error starting task:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Starten der Aufgabe'));
    }
  };

  const openCompleteModal = (task) => {
    setCompletingTask(task);
    setCompleteData(emptyComplete);
    setFormError(null);
    setShowCompleteModal(true);
  };

  const handleComplete = async (e) => {
    e.preventDefault();
    if (saving || !completingTask) return;
    setSaving(true);
    setFormError(null);
    try {
      await updateMediaTaskStatus(completingTask.id, {
        status: 'completed',
        notes: completeData.notes || undefined,
        proof_url: completeData.proof_url || undefined
      });
      setShowCompleteModal(false);
      setCompletingTask(null);
      setCompleteData(emptyComplete);
      toast.success('Aufgabe abgeschlossen');
      fetchTasks();
    } catch (error) {
      console.error('Error completing task:', error);
      setFormError(extractErrorMessage(error, 'Fehler beim Abschlie\u00dfen der Aufgabe'));
    } finally {
      setSaving(false);
    }
  };

  const handleFail = async (id) => {
    try {
      await updateMediaTaskStatus(id, { status: 'failed' });
      toast.success('Aufgabe als fehlgeschlagen markiert');
      fetchTasks();
    } catch (error) {
      console.error('Error failing task:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Markieren der Aufgabe'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Medien-Aufgabe wirklich l\u00f6schen?')) return;
    try {
      await deleteMediaTask(id);
      toast.success('Medien-Aufgabe gel\u00f6scht');
      fetchTasks();
    } catch (error) {
      console.error('Error deleting media task:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim L\u00f6schen der Medien-Aufgabe'));
    }
  };

  const closeCreateModal = () => {
    if (saving) return;
    setShowCreateModal(false);
    setFormError(null);
  };

  const closeCompleteModal = () => {
    if (saving) return;
    setShowCompleteModal(false);
    setCompletingTask(null);
    setFormError(null);
  };

  const getTaskSummary = (task) => {
    const parts = [];
    if (task.type === 'edge' && task.edge_count) parts.push(`${task.edge_count}x Edging`);
    if (task.duration_minutes) parts.push(`${task.duration_minutes} Min.`);
    if (task.conditions) {
      const cumMatch = CUM_CONDITIONS.find(c => c.value === task.conditions);
      const worshipMatch = WORSHIP_CONDITIONS.find(c => c.value === task.conditions);
      if (cumMatch) parts.push(cumMatch.label);
      else if (worshipMatch) parts.push(worshipMatch.label);
      else parts.push(task.conditions);
    }
    if (task.instructions) parts.push(task.instructions);
    return parts.join(' \u2013 ') || '\u2013';
  };

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        <div className="page-header">
          <h1>Medien-Aufgaben</h1>
          <button className="add-btn" onClick={() => setShowCreateModal(true)}>
            <FaPlus /> Neue Aufgabe
          </button>
        </div>

        <div className="filter-tabs">
          <button className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>Alle</button>
          <button className={filter === 'pending' ? 'active' : ''} onClick={() => setFilter('pending')}>Ausstehend</button>
          <button className={filter === 'active' ? 'active' : ''} onClick={() => setFilter('active')}>Aktiv</button>
          <button className={filter === 'completed' ? 'active' : ''} onClick={() => setFilter('completed')}>Abgeschlossen</button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : tasks.length === 0 ? (
          <div className="no-data">Keine Medien-Aufgaben gefunden</div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Datum</th>
                  <th>Benutzer</th>
                  <th>Typ</th>
                  <th>Aufgabe</th>
                  <th>Status</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map(task => {
                  const typeConf = TYPE_CONFIG[task.type] || TYPE_CONFIG.custom;
                  const statusConf = STATUS_CONFIG[task.status] || STATUS_CONFIG.pending;
                  return (
                    <tr key={task.id}>
                      <td>
                        {new Date(task.created_at).toLocaleDateString('de-DE', {
                          day: '2-digit', month: '2-digit', year: 'numeric'
                        })}
                      </td>
                      <td>{resolveUser(task.user_id)}</td>
                      <td>
                        <span className="badge" style={{ background: typeConf.color, color: '#fff' }}>
                          {typeConf.label}
                        </span>
                      </td>
                      <td style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {getTaskSummary(task)}
                      </td>
                      <td>
                        <span className="badge" style={{ background: statusConf.color, color: '#fff' }}>
                          {statusConf.label}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          {task.status === 'pending' && (
                            <button className="action-btn" onClick={() => handleStart(task.id)} title="Starten" style={{ color: '#7289da' }}>
                              <FaPlay />
                            </button>
                          )}
                          {task.status === 'active' && (
                            <>
                              <button className="action-btn complete" onClick={() => openCompleteModal(task)} title="Abschlie\u00dfen" style={{ color: '#43b581' }}>
                                <FaCheck />
                              </button>
                              <button className="action-btn fail" onClick={() => handleFail(task.id)} title="Fehlgeschlagen" style={{ color: '#f04747' }}>
                                <FaTimes />
                              </button>
                            </>
                          )}
                          <button className="action-btn delete" onClick={() => handleDelete(task.id)} title="L\u00f6schen" style={{ color: '#f04747' }}>
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <div className="modal-overlay" onClick={closeCreateModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>Neue Medien-Aufgabe</h2>
              {formError && <div className="shared-form-error-banner">{formError}</div>}
              <form onSubmit={handleCreate}>
                <div className="form-group">
                  <UserSelect
                    value={newTask.user_id}
                    onChange={id => setNewTask({ ...newTask, user_id: id })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Typ</label>
                  <select
                    value={newTask.type}
                    onChange={e => setNewTask({ ...newTask, type: e.target.value, conditions: '', edge_count: '', duration_minutes: '', instructions: '' })}
                  >
                    <option value="edge">Edge</option>
                    <option value="cum">Cum Tribute</option>
                    <option value="worship">Anbetung</option>
                    <option value="custom">Eigene</option>
                  </select>
                </div>

                {/* Edge fields */}
                {newTask.type === 'edge' && (
                  <>
                    <div className="form-group">
                      <label>Anzahl Edges *</label>
                      <input
                        type="number"
                        min="1"
                        value={newTask.edge_count}
                        onChange={e => setNewTask({ ...newTask, edge_count: e.target.value })}
                        required
                        placeholder="z.B. 5"
                      />
                    </div>
                    <div className="form-group">
                      <label>Dauer (Minuten)</label>
                      <input
                        type="number"
                        min="1"
                        value={newTask.duration_minutes}
                        onChange={e => setNewTask({ ...newTask, duration_minutes: e.target.value })}
                        placeholder="Optional"
                      />
                    </div>
                    <div className="form-group">
                      <label>Anweisungen</label>
                      <textarea
                        value={newTask.instructions}
                        onChange={e => setNewTask({ ...newTask, instructions: e.target.value })}
                        placeholder="Zus\u00e4tzliche Anweisungen..."
                      />
                    </div>
                  </>
                )}

                {/* Cum fields */}
                {newTask.type === 'cum' && (
                  <>
                    <div className="form-group">
                      <label>Bedingung</label>
                      <select
                        value={newTask.conditions}
                        onChange={e => setNewTask({ ...newTask, conditions: e.target.value })}
                      >
                        <option value="">-- Ausw\u00e4hlen --</option>
                        {CUM_CONDITIONS.map(c => (
                          <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Anweisungen</label>
                      <textarea
                        value={newTask.instructions}
                        onChange={e => setNewTask({ ...newTask, instructions: e.target.value })}
                        placeholder="Zus\u00e4tzliche Anweisungen..."
                      />
                    </div>
                  </>
                )}

                {/* Worship fields */}
                {newTask.type === 'worship' && (
                  <>
                    <div className="form-group">
                      <label>Bedingung</label>
                      <select
                        value={newTask.conditions}
                        onChange={e => setNewTask({ ...newTask, conditions: e.target.value })}
                      >
                        <option value="">-- Ausw\u00e4hlen --</option>
                        {WORSHIP_CONDITIONS.map(c => (
                          <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Anweisungen</label>
                      <textarea
                        value={newTask.instructions}
                        onChange={e => setNewTask({ ...newTask, instructions: e.target.value })}
                        placeholder="Zus\u00e4tzliche Anweisungen..."
                      />
                    </div>
                    <div className="form-group">
                      <label>Dauer (Minuten)</label>
                      <input
                        type="number"
                        min="1"
                        value={newTask.duration_minutes}
                        onChange={e => setNewTask({ ...newTask, duration_minutes: e.target.value })}
                        placeholder="Optional"
                      />
                    </div>
                  </>
                )}

                {/* Custom fields */}
                {newTask.type === 'custom' && (
                  <>
                    <div className="form-group">
                      <label>Anweisungen *</label>
                      <textarea
                        value={newTask.instructions}
                        onChange={e => setNewTask({ ...newTask, instructions: e.target.value })}
                        required
                        placeholder="Beschreibe die Aufgabe..."
                      />
                    </div>
                    <div className="form-group">
                      <label>Dauer (Minuten)</label>
                      <input
                        type="number"
                        min="1"
                        value={newTask.duration_minutes}
                        onChange={e => setNewTask({ ...newTask, duration_minutes: e.target.value })}
                        placeholder="Optional"
                      />
                    </div>
                  </>
                )}

                <div className="form-group">
                  <label>Medien-ID (optional)</label>
                  <input
                    type="number"
                    value={newTask.media_id}
                    onChange={e => setNewTask({ ...newTask, media_id: e.target.value })}
                    placeholder="Verlinkte Medien-ID"
                  />
                </div>

                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeCreateModal} disabled={saving}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={saving}>
                    {saving ? 'Speichert\u2026' : 'Erstellen'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Complete Modal */}
        {showCompleteModal && completingTask && (
          <div className="modal-overlay" onClick={closeCompleteModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>Aufgabe abschlie\u00dfen</h2>
              {formError && <div className="shared-form-error-banner">{formError}</div>}
              <form onSubmit={handleComplete}>
                <div className="form-group">
                  <label>Notizen</label>
                  <textarea
                    value={completeData.notes}
                    onChange={e => setCompleteData({ ...completeData, notes: e.target.value })}
                    placeholder="Anmerkungen zur Aufgabe..."
                  />
                </div>
                <div className="form-group">
                  <label>Beweis-URL</label>
                  <input
                    type="text"
                    value={completeData.proof_url}
                    onChange={e => setCompleteData({ ...completeData, proof_url: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeCompleteModal} disabled={saving}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={saving}>
                    {saving ? 'Speichert\u2026' : 'Abschlie\u00dfen'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default MediaTasks;
